import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { VehicleRosbagSaveModeStatus } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Bag保存モード変更API
 */
export const useUpdateBagSaveModeAPI = (): {
  state: APIState;
  postUpdateBagSaveMode: (
    vehicleId: string,
    mode: VehicleRosbagSaveModeStatus,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postUpdateBagSaveModeAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      mode: VehicleRosbagSaveModeStatus,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/update_rosbag_save_mode`,
          {
            mode,
          },
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postUpdateBagSaveMode = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, mode: VehicleRosbagSaveModeStatus) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_vehicle_update_rosbag_save_mode', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postUpdateBagSaveModeAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          mode,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_update_rosbag_save_mode', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_vehicle_update_rosbag_save_mode', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        notifySuccess,
        postUpdateBagSaveModeAPI,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    postUpdateBagSaveMode,
  };
};
